import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { products as productsApi, API_URL } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'sonner';
import {
  Info, X, ChevronLeft, ChevronRight, Eye, Lock, ZoomIn
} from 'lucide-react';

const CONDITION_INFO = {
  'Excellent': 'Like new condition. Minimal signs of wear. Fabric, stitching, and details all intact.',
  'Good': 'Light signs of wear consistent with age. Minor fading or marks that add character.',
  'Vintage Worn': 'Visible signs of wear that reflect authentic vintage character. May have fading, small marks, or minor repairs.',
  'Fair': 'Noticeable wear. Suited for those who appreciate the raw vintage aesthetic.',
};

const FIT_LABELS = {
  'S': 'Slim Fit',
  'M': 'Regular Fit',
  'L': 'Oversized',
  'XL': 'Oversized',
  'OS': 'One Size — Oversized',
};

function generateStory(product) {
  if (product.story && product.story.length > 20) return product.story;
  const decades = ['1980s', '1990s', 'Early 2000s', 'Late 1990s', 'Mid-1990s'];
  const sources = ['a warehouse clearance in Manchester', 'a Portobello Road stall', 'a private collection in Milan', 'a vintage dealer in Paris', 'an estate sale in East London', 'a deadstock lot in Amsterdam'];
  const details = {
    'Jackets': 'Heavy-duty construction. Patina you can\'t replicate. Worn in, not worn out.',
    'T-Shirts': 'Single stitch collar. Paper-thin cotton. The kind of fade that takes 30 years to earn.',
    'Streetwear': 'Original tags still intact on the inner label. A time capsule from an era that defined street culture.',
    'Vintage': 'Every thread tells a story. This is the real thing — not a reproduction.',
  };
  const era = product.decade || decades[Math.floor(Math.random() * decades.length)];
  const source = sources[Math.floor(Math.random() * sources.length)];
  const detail = details[product.category] || 'One of maybe a dozen left in this condition anywhere in the world.';
  return `Circa ${era}. Found at ${source}. ${detail}`;
}

export const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart, drawerOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [adding, setAdding] = useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showConditionTip, setShowConditionTip] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [completeTheLook, setCompleteTheLook] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const ctaRef = useRef(null);

  const viewers = useMemo(() => Math.floor(Math.random() * 8) + 4, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await productsApi.getOne(id);
        setProduct(data);
        setSelectedImage(0);
        setSelectedSize('');

        const viewed = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
        const updated = [data.id, ...viewed.filter(v => v !== data.id)].slice(0, 8);
        localStorage.setItem('recently_viewed', JSON.stringify(updated));

        const all = await productsApi.getAll();
        const sameCat = all.filter(p => p.id !== data.id && p.category === data.category);
        setRelatedProducts(sameCat.slice(0, 4));
        // Complete the Look: different category but similar price range for cross-sell
        const otherCat = all.filter(p => p.id !== data.id && p.category !== data.category);
        setCompleteTheLook(otherCat.slice(0, 3));

        const rvIds = JSON.parse(localStorage.getItem('recently_viewed') || '[]').filter(v => v !== data.id).slice(0, 4);
        if (rvIds.length > 0) {
          setRecentlyViewed(all.filter(p => rvIds.includes(p.id)).slice(0, 4));
        }
      } catch (error) {
        console.error('Failed to load product', error);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, [product]);

  const handleAddToCart = async () => {
    if (!selectedSize) return toast.error('Please select a size');
    setAdding(true);
    try {
      await addToCart(product.id, selectedSize, 1);
      toast.success('Added to bag');
    } catch {
      toast.error('Something went wrong');
    }
    setAdding(false);
  };

  const handleQuickAdd = async (p) => {
    const sz = p.sizes?.[0] || p.size || 'OS';
    try {
      await addToCart(p.id, sz, 1);
      toast.success(`${p.name} added to bag`);
    } catch {
      toast.error('Something went wrong');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="product-loading">
        <div className="w-8 h-8 border-2 border-[#E2E2E2] border-t-[#C9A84C] rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center" data-testid="product-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold mb-2">Product Not Found</h1>
          <Link to="/shop" className="text-[#C9A84C] font-medium text-sm hover:underline">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const images = (product.images && product.images.length > 0)
    ? product.images.map(img => img.startsWith('http') ? img : `${API_URL}${img}`)
    : ['https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800'];

  const conditionGrade = product.condition_grade || 'Excellent';
  const conditionDesc = CONDITION_INFO[conditionGrade] || CONDITION_INFO['Good'];
  const storyText = generateStory(product);
  const isSoldOut = product.stock === 0;
  const fitLabel = FIT_LABELS[product.sizes?.[0] || product.size || 'M'] || 'Regular Fit';

  return (
    <div className="bg-[#F9F8F6] min-h-screen" data-testid="product-detail-page">
      <SEO title={product.name} description={(product.description||'').substring(0,160)} path={`/product/${id}`} image={product.images && product.images[0]} product={product} type="product" />

      <div className="container-custom py-4 md:py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[11px] text-[#666] mb-4 md:mb-6" data-testid="breadcrumb">
          <Link to="/" className="hover:text-[#0A0A0A] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#0A0A0A] transition-colors">Shop</Link>
          {product.category && (<><span>/</span><Link to={`/shop?category=${product.category}`} className="hover:text-[#0A0A0A] transition-colors">{product.category}</Link></>)}
          <span>/</span>
          <span className="text-[#0A0A0A] font-medium truncate max-w-[200px]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10">
          {/* LEFT: Image Gallery — 60% */}
          <div className="md:col-span-3" data-testid="product-gallery">
            <div
              className="relative aspect-[3/4] bg-[#F0EEEB] overflow-hidden cursor-zoom-in mb-3"
              onClick={() => setShowLightbox(true)}
              data-testid="product-main-image"
            >
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover transition-opacity duration-300"
                loading="eager"
                width={800}
                height={1067}
              />
              {product.stock === 1 && (
                <div className="absolute top-4 left-4 bg-red-600 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5" data-testid="only-one-left-badge">
                  Only 1 Left
                </div>
              )}
              {isSoldOut && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="bg-white text-[#0A0A0A] text-sm font-bold uppercase tracking-widest px-6 py-3">Sold</span>
                </div>
              )}
              <button
                className="absolute bottom-4 right-4 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors btn-icon"
                onClick={(e) => { e.stopPropagation(); setShowLightbox(true); }}
                data-testid="zoom-btn"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide" data-testid="thumbnail-strip">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`flex-shrink-0 w-16 h-20 md:w-20 md:h-24 overflow-hidden border-2 transition-all btn-icon ${
                      selectedImage === i ? 'border-[#0A0A0A]' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${i}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" width={80} height={96} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Product Info — 40% */}
          <div className="md:col-span-2" data-testid="product-info">
            {/* Brand */}
            {product.brand && product.brand !== 'Unknown Brand' && product.brand !== 'Unknown' && (
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#666] mb-1" data-testid="product-brand">
                {product.brand}
              </p>
            )}

            {/* Title */}
            <h1 className="text-2xl md:text-[32px] font-heading font-bold leading-tight tracking-tight mb-3" data-testid="product-title">
              {product.name}
            </h1>

            {/* Price */}
            <p className="text-xl md:text-2xl font-bold mb-4" data-testid="product-price">
              &pound;{product.price?.toFixed(2)}
            </p>

            {/* SCARCITY — LOUD */}
            {!isSoldOut && product.stock <= 5 && (
              <div className="mb-4" data-testid="scarcity-notice">
                <p className="text-red-600 text-lg md:text-xl font-bold uppercase tracking-wide leading-tight">
                  Only {product.stock} available — this will not restock
                </p>
                <p className="text-red-500/70 text-sm mt-1">When it's gone, it's gone forever.</p>
              </div>
            )}

            {/* Condition */}
            <div className="flex items-center gap-2 mb-3 relative" data-testid="product-condition">
              <span className="text-xs font-semibold uppercase tracking-wider">Condition:</span>
              <span className={`text-xs font-bold uppercase tracking-wider ${
                conditionGrade === 'Excellent' ? 'text-green-700' :
                conditionGrade === 'Good' ? 'text-[#C9A84C]' : 'text-orange-600'
              }`}>
                {conditionGrade}
              </span>
              <button
                className="relative btn-icon"
                style={{ minHeight: 'unset' }}
                onClick={() => setShowConditionTip(!showConditionTip)}
                data-testid="condition-info-btn"
              >
                <Info className="w-3.5 h-3.5 text-[#999]" />
              </button>
              {showConditionTip && (
                <div className="absolute z-30 top-full left-0 mt-1 bg-white border border-[#E2E2E2] shadow-lg p-3 rounded max-w-[280px] text-xs text-[#666] leading-relaxed" data-testid="condition-tooltip">
                  <p className="font-semibold text-[#0A0A0A] mb-1">{conditionGrade}</p>
                  <p>{conditionDesc}</p>
                  <button onClick={() => setShowConditionTip(false)} className="mt-2 text-[#C9A84C] font-semibold text-[10px] uppercase btn-icon" style={{ minHeight: 'unset' }}>
                    Got it
                  </button>
                </div>
              )}
            </div>

            {/* Viewers */}
            <div className="flex items-center gap-2 text-xs text-[#666] mb-5" data-testid="product-viewers">
              <Eye className="w-3.5 h-3.5 text-red-500" strokeWidth={1.5} />
              <span><strong className="text-[#0A0A0A]">{viewers} people</strong> viewing this right now</span>
            </div>

            {/* Story — emotional buy */}
            <div className="border-l-2 border-[#C9A84C] pl-4 py-2 mb-5 bg-[#F9F8F6]" data-testid="product-story">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] font-semibold mb-1.5">The Story</p>
              <p className="text-sm italic text-[#444] leading-relaxed">{storyText}</p>
            </div>

            {/* Description */}
            {product.description && (
              <p className="text-sm text-[#666] leading-relaxed mb-5" data-testid="product-description">
                {product.description}
              </p>
            )}

            {/* Size & Fit */}
            <div className="mb-5" data-testid="size-fit-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider">Size & Fit</span>
                <button
                  onClick={() => setShowSizeGuide(true)}
                  className="text-[11px] text-[#C9A84C] font-semibold hover:underline btn-icon"
                  style={{ minHeight: 'unset' }}
                  data-testid="size-guide-link"
                >
                  Size Guide
                </button>
              </div>
              {(product.sizes?.length > 0 || product.size) && (
                <div className="flex gap-2 flex-wrap mb-2">
                  {(product.sizes || [product.size]).filter(Boolean).map((sz) => (
                    <button
                      key={sz}
                      className={`px-6 py-3 border text-sm font-semibold transition-all ${
                        selectedSize === sz
                          ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white'
                          : 'border-[#E2E2E2] hover:border-[#0A0A0A]'
                      }`}
                      onClick={() => setSelectedSize(sz)}
                      data-testid={`size-btn-${sz}`}
                    >
                      {sz}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#666]"><strong>{fitLabel}</strong></p>
              <p className="text-xs text-[#999] italic mt-0.5">
                {product.size_guide || `Fits true to vintage sizing. When in doubt, size up.`}
              </p>
            </div>

            {/* Trust Block — aggressive, clean, no icons */}
            <div className="space-y-2 mb-5 py-4 border-t border-b border-[#E2E2E2]" data-testid="trust-block">
              <p className="text-[13px] font-semibold text-[#0A0A0A]">&#10003; Authentic vintage — guaranteed</p>
              <p className="text-[13px] font-semibold text-[#0A0A0A]">&#10003; One-of-a-kind piece — never restocked</p>
              <p className="text-[13px] font-semibold text-[#0A0A0A]">&#10003; Free returns within 14 days</p>
              <p className="text-[13px] font-semibold text-[#0A0A0A]">&#10003; Dispatched within 48 hours</p>
            </div>

            {/* Add to Cart / Sold Out */}
            <div ref={ctaRef}>
              {isSoldOut ? (
                <Link to={`/shop?category=${product.category || ''}`} className="block">
                  <button
                    className="w-full bg-[#666] text-white text-sm font-semibold uppercase tracking-[0.15em] flex items-center justify-center gap-2"
                    style={{ minHeight: '52px' }}
                    data-testid="sold-out-btn"
                  >
                    Sold — View Similar Pieces
                  </button>
                </Link>
              ) : (
                <button
                  onClick={handleAddToCart}
                  disabled={adding}
                  className="w-full bg-[#0A0A0A] text-white text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  style={{ minHeight: '52px' }}
                  data-testid="add-to-cart-btn"
                >
                  {adding ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : 'Add to Bag'}
                </button>
              )}
            </div>

            {/* Secure checkout line */}
            <div className="flex items-center justify-center gap-1.5 mt-3 mb-2" data-testid="secure-checkout-line">
              <Lock className="w-3 h-3 text-[#999]" strokeWidth={1.5} />
              <span className="text-[11px] text-[#999]">Secure checkout</span>
            </div>

            {/* SKU */}
            {product.sku && (
              <p className="text-[10px] text-[#999] uppercase tracking-wider" data-testid="product-sku">
                SKU: {product.sku}
              </p>
            )}
          </div>
        </div>

        {/* ─── COMPLETE THE LOOK (AOV booster) ─── */}
        {completeTheLook.length > 0 && (
          <section className="mt-14 mb-8 border-t border-[#E2E2E2] pt-10" data-testid="complete-the-look">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="text-[9px] uppercase tracking-[0.3em] text-[#C9A84C] font-semibold block">Style It</span>
                <h2 className="text-xl md:text-2xl font-heading font-bold tracking-tight mt-1">Complete the Look</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {completeTheLook.map(p => {
                const img = p.images?.[0] ? (p.images[0].startsWith('http') ? p.images[0] : `${API_URL}${p.images[0]}`) : 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400';
                return (
                  <div key={p.id} className="group" data-testid={`ctl-item-${p.id}`}>
                    <Link to={`/product/${p.id}`} className="block">
                      <div className="aspect-[3/4] bg-[#F0EEEB] overflow-hidden mb-2 relative">
                        <img src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width={400} height={533} />
                        {p.stock === 1 && (
                          <div className="absolute top-2 left-2 bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5">Only 1 Left</div>
                        )}
                        {p.condition_grade && (
                          <div className="absolute bottom-2 left-2 bg-white/90 text-[8px] font-semibold uppercase tracking-wider px-1.5 py-0.5">{p.condition_grade}</div>
                        )}
                      </div>
                    </Link>
                    <p className="text-sm font-medium truncate mb-0.5">{p.name}</p>
                    <p className="text-sm font-bold mb-2">&pound;{p.price?.toFixed(2)}</p>
                    <button
                      onClick={() => handleQuickAdd(p)}
                      className="w-full bg-[#0A0A0A] text-white text-[11px] font-semibold uppercase tracking-[0.1em] hover:bg-[#1a1a1a] transition-colors"
                      style={{ minHeight: '40px' }}
                      data-testid={`ctl-add-${p.id}`}
                    >
                      Add to Bag
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── YOU MAY ALSO LIKE ─── */}
        {relatedProducts.length > 0 && (
          <section className="mt-10 mb-8 border-t border-[#E2E2E2] pt-10" data-testid="related-products">
            <h2 className="text-xl md:text-2xl font-heading font-bold tracking-tight mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}

        {/* ─── RECENTLY VIEWED ─── */}
        {recentlyViewed.length > 0 && (
          <section className="mt-8 mb-8 border-t border-[#E2E2E2] pt-10" data-testid="recently-viewed-pdp">
            <h2 className="text-xl md:text-2xl font-heading font-bold tracking-tight mb-6">Recently Viewed</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {recentlyViewed.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </div>

      {/* Sticky Add to Cart — Mobile only, hidden when drawer open */}
      {showStickyBar && !drawerOpen && !isSoldOut && (
        <div className="fixed bottom-14 sm:bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E2E2E2] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 sm:hidden" data-testid="sticky-cart-bar">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">&pound;{product.price?.toFixed(2)}</p>
              <p className="text-[10px] text-[#666] truncate">{product.name}</p>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={adding}
              className="bg-[#0A0A0A] text-white px-6 text-xs font-semibold uppercase tracking-[0.1em] flex-shrink-0 disabled:opacity-50"
              style={{ minHeight: '48px' }}
              data-testid="sticky-add-to-cart-btn"
            >
              {adding ? 'Adding...' : 'Add to Bag'}
            </button>
          </div>
        </div>
      )}

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="size-guide-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSizeGuide(false)} />
          <div className="relative bg-white max-w-lg w-full mx-4 p-6 md:p-8 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold">Size Guide</h3>
              <button onClick={() => setShowSizeGuide(false)} className="btn-icon" style={{ minHeight: 'unset' }} data-testid="size-guide-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <table className="w-full text-sm border-collapse" data-testid="size-guide-table">
              <thead>
                <tr className="border-b border-[#E2E2E2]">
                  <th className="text-left py-2.5 pr-4 font-semibold text-xs uppercase tracking-wider">Measurement</th>
                  <th className="text-center py-2.5 px-2 font-semibold text-xs uppercase tracking-wider">S</th>
                  <th className="text-center py-2.5 px-2 font-semibold text-xs uppercase tracking-wider">M</th>
                  <th className="text-center py-2.5 px-2 font-semibold text-xs uppercase tracking-wider">L</th>
                  <th className="text-center py-2.5 px-2 font-semibold text-xs uppercase tracking-wider">XL</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Pit-to-Pit', vals: ['48cm', '52cm', '56cm', '60cm'] },
                  { label: 'Length', vals: ['68cm', '72cm', '74cm', '76cm'] },
                  { label: 'Sleeve', vals: ['60cm', '62cm', '64cm', '66cm'] },
                  { label: 'Shoulder', vals: ['44cm', '46cm', '48cm', '50cm'] },
                ].map(({ label, vals }) => (
                  <tr key={label} className="border-b border-[#E2E2E2]/50">
                    <td className="py-2.5 pr-4 text-[#666]">{label}</td>
                    {vals.map((v, i) => <td key={i} className="text-center py-2.5 px-2">{v}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 pt-4 border-t border-[#E2E2E2]">
              <p className="text-xs font-semibold text-[#0A0A0A] mb-1">Fit Note</p>
              <p className="text-xs text-[#666] italic">Worn here on a 6ft medium. {fitLabel}. Vintage sizing may vary from modern equivalents — when in doubt, size up.</p>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="lightbox" onClick={() => setShowLightbox(false)}>
          <button className="absolute top-4 right-4 text-white btn-icon" style={{ minHeight: 'unset' }} onClick={() => setShowLightbox(false)} data-testid="lightbox-close">
            <X className="w-6 h-6" />
          </button>
          {images.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors btn-icon" onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage - 1 + images.length) % images.length); }} data-testid="lightbox-prev">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors btn-icon" onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage + 1) % images.length); }} data-testid="lightbox-next">
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <img src={images[selectedImage]} alt={product.name} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs">
            {selectedImage + 1} / {images.length}
          </div>
        </div>
      )}
    </div>
  );
};
